// NPM
import React, { useEffect, useMemo, useState } from 'react';
import {Link, useHistory, useParams} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isNil, map, isEmpty, find, cloneDeep, groupBy, forEach } from 'lodash';
import { useRecoilState } from 'recoil';
import {
  EmailIcon, EmailShareButton,
  FacebookIcon,
  FacebookShareButton,
  FacebookShareCount, InstapaperIcon, InstapaperShareButton,
  WhatsappIcon,
  WhatsappShareButton
} from "react-share";

// ATOM
import { authActionStatusAtom, authPassportAtom, authErrorAtom, isAuthAtom } from 'Recoil/auth.atom';
import { LocalCartAtom } from 'Recoil/order.atom'
import { statusChatAtom } from 'Recoil/chat.atom';

// ANT COMPONENT
import { Alert } from 'antd';
import Icon, {
  PlusCircleOutlined,
  MinusCircleOutlined,
  ArrowRightOutlined
} from '@ant-design/icons';
import { Space, Row, Col, Radio, Divider } from 'antd';
import { Button, Tabs, BasicModal, SkeletonLoader } from '@life-line-lab/components-react';

// SERVICE
import { authBusinessAccountLogin, authLogin } from 'Services/auth.services';

// HELPER
import { toaster, generateCode, encode } from 'Helpers/index';
import { getAccessToken, getProfileID } from 'Helpers/session';

// COMPONENT
import LoginForm from 'Components/auth/login-form';
import VoucherDeals from 'Components/voucher-deals';

// HOOK
import useSession from 'Hooks/session.hook';

// STYLE
import './product-details.scss';
import '../product.scss';
import VacationInfoBox from 'Components/shared/vacation-info-box/vacation-info-box';
import Slider from "react-slick";
import { RiTruckLine } from 'react-icons/ri';
import ProductPricing from '../product-pricing';

interface ProductDetailsProps {
  productData: any;
  handleAddToCart: ({ }: any) => void;
  handleUpdateToCart: ({ }: any) => void;
  cartList: any
  handleFetchCart: () => void
}

const ProductDetails: React.FC<ProductDetailsProps> = ({ handleFetchCart, cartList, productData, handleUpdateToCart, handleAddToCart }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [weightValidation, setWeightValidation] = useState(false);
  const [localCartState, setLocalCartState] = useRecoilState<any>(LocalCartAtom);
  const initialInput = {
    quantity: 1
  }
  const initialError = {}
  const [input, setInput] = useState<any>(initialInput);
  const [error, setError] = useState(initialError);
  const [cartData, setCartData] = useState<any>([]);
  const [cartAlreadyAdded, setAlreadyAdded] = useState(false);
  const [lastInputQuantity, setLastInputQuantity] = useState(initialInput.quantity);

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [loginStatus, setLoginStatus] = useRecoilState<any>(authActionStatusAtom);
  const [loginError, setLoginError] = useRecoilState<any>(authErrorAtom);

  const savedCartData = JSON.parse(localStorage.getItem('cartData') || '{}') || [];
  const profileID = getProfileID()
  const { id } = useParams<{ id: string }>();


  useEffect(() => {
    setLoadingStatus(false)
    if (!isEmpty(productData)) {
      let featuredImage = '';
      let imageList = map(cloneDeep(productData.productImages), (imageObj, i) => {
        if (imageObj.feature) {
          featuredImage = imageObj.image;
        }
        return imageObj;
      })

      imageList = [
        ...imageList.filter(o => o.feature),  // feature image must be first
        ...imageList.filter(o => !o.feature),
      ]

      setInput({
        // ...input,  // should not use previous input when the prop changed, may contain stale data
        ...initialInput,
        ...productData,
        images: imageList,
        featuredImage: featuredImage,
        selectedVariant: 0,
        selectedVariantObj: productData.stockVariants[0] || {}
      })
    }

  }, [productData])

  // useEffect(() => {
  //   if (!isEmpty(savedCartData)) {
  //     setCartData(savedCartData)
  //   }
  // }, [])

  useEffect(() => {
    if (!isEmpty(cartList.items)) {
      setLoadingStatus(false)
      setCartData(cartList.items)
    }
  }, [cartList.items])

  //   useEffect(()=>{
  //     window.scrollTo({
  //       top: 0,
  //     })
  //  },[])

  // useEffect(() => {
  //   if (!isEmpty(cartData) && !isNil(input.selectedVariantObj)) {
  // let alreadyAdded = find(cartData, cartObj => {
  //   return cartObj.selectedVariant.variantName == input.selectedVariantObj.variantName
  //     && cartObj.productData.inventoryID == productData.inventoryID;
  // })

  // if (alreadyAdded) {
  //   setAlreadyAdded(true)
  // } else {
  //   setAlreadyAdded(false)
  // }
  //   }
  // }, [cartData, input.selectedVariantObj])

  const handleVariantRadioChange = (value: any, name: string, objName: any) => {
    setError(initialError);
    // change variant image upon variant selection
    const variantImg = find(input.productImages, (productImg) => {
      return productImg.variantCode === input?.stockVariants[value].variantCode;
    })?.image;
    setInput({
      ...input, [name]: value, [objName]: input?.stockVariants[value], ['quantity']: 1, featuredImage: variantImg === undefined ? input.featuredImage === null ? '/assets/images/product-placeholder.png' : input.featuredImage : variantImg,
    });
  }

  const addQuantity = () => {
    if (input.quantity >= parseInt(input.selectedVariantObj?.restrictedQtyLimit)) {
      toaster('error', t('error.overRestrictedLimit', { amount: parseInt(input.selectedVariantObj?.restrictedQtyLimit) }));
      return;
    }
    if (input.quantity >= input.selectedVariantObj.stockLimit) {
      return;
    }
    setInput({ ...input, ['quantity']: input.quantity + 1 });
  }

  const reduceQuantity = () => {
    if (input.quantity == 1) {
      return;
    }
    setInput({ ...input, ['quantity']: input.quantity - 1 });
  }

  const onImageSelect = (imageObj) => {
    const imageList = map(input.images, img => {
      img.selected = img.fileID == imageObj.fileID ? true : false;
      return img;
    })
    setInput({ ...input, 'featuredImage': imageObj.image, 'images': imageList });
  }

  const submitToCart = (data) => {
    if (cartList?.items?.length == 0 || cartList?.items?.length) {
      let obj = {
        cardId: cartList.cartID,
        items: data
      }
      handleUpdateToCart(obj)
    } else {
      let obj = {
        items: data
      }
      handleAddToCart(obj)
    }
  }

  const [loadingStatus, setLoadingStatus] = useState(false)

  const onAddCart = () => {
    if (input.disablePurchase || input.selectedVariantObj?.stockLimit == 0) {
      return;
    }
    const session = useSession();
    if (isEmpty(session)) {
      onOpenModal();
      return;
    }
    setLoadingStatus(true)

    let clonedCartData: any = cloneDeep(cartData)

    let alreadyAdded = find(cartData, cartObj => {
      return cartObj.selectedVariant.variantCode == input.selectedVariantObj.variantCode
        && cartObj.productData.inventoryID == productData.inventoryID;
    })

    if (alreadyAdded) {

      if (alreadyAdded.quantity + input.quantity > input.selectedVariantObj.stockLimit) {
        toaster("warning", t('toaster.selectedQuantityError'));
        setLoadingStatus(false)
        return;
      }
      clonedCartData.map((cartObj) => {
        if ((cartObj.selectedVariant.variantCode == input.selectedVariantObj.variantCode) && (cartObj.productData.inventoryID == productData.inventoryID)) {
          return cartObj.quantity += input.quantity
        }
      })

      let supplierGroup = groupBy(clonedCartData, cartObj => {
        return cartObj.productData.orgID;
      })

      let shippingCostItems = map(supplierGroup, (data, supplierID) => {
        let weight = 0;
        data.map((item) => {
          weight += (item.weight * parseFloat(item.quantity))
        })
        return {
          supplierID: supplierID,
          state: session.profile.primaryAddress.state,
          weight: weight
        }
      })

      let totalWeight = 0;
      map(shippingCostItems, (obj) => {
        totalWeight += obj.weight
      })

      if (totalWeight <= 28) {
        submitToCart(clonedCartData);
      } else {
        setLoadingStatus(false)
        setWeightValidation(true)
        setTimeout(() => {
          setWeightValidation(false)
        }, 5000);
      }
    } else {
      let cartDetails = {
        cartID: generateCode('CRT-'),
        productData: productData,
        selectedVariant: input.selectedVariantObj,
        quantity: input.quantity
      }
      clonedCartData.push(cartDetails)
      submitToCart(clonedCartData)
    }

  }

  const onBuyNow = () => {
    if (input.disablePurchase || input.selectedVariantObj?.stockLimit == 0) {
      return;
    }
    const session = useSession();
    if (isEmpty(session)) {
      onOpenModal();
    }
    else if (session) {
      let productUOMs: any = {};
      if (!isEmpty(productData.product?.baseUOM) || !isEmpty(productData.product?.stockUOMs)) {
        productUOMs[productData.product?.baseUOM.uom] = productData.product?.baseUOM.weight?.value || "0"
        forEach(productData.product?.stockUOMs, uomObj => {
          productUOMs[uomObj.uom] = uomObj.weight?.value || "0"
        })
      }
      let itemWeight = productData.stockVariants[0]?.weight?.value ?? productData.stockVariants[0]?.weight ??
        productUOMs[productData.stockVariants[0].uom];

      let productType = productData.product.productType;

      let cartDetails = {
        cartID: generateCode('CRT-'),
        productData: productData,
        productType: productType,
        selectedVariant: input.selectedVariantObj,
        variantImg: find(productData.productImages, (variant) => {
          return variant.variantCode === input.selectedVariantObj.variantCode
        })?.image || productData.featuredImage?.image || '/assets/images/product-placeholder.png',
        quantity: input.quantity,
        weight: productType != 'VOUCHER' && productType !== 'NO_SHIPPING' ? parseFloat(itemWeight) * parseFloat(input.quantity) : 0
      }

      //localStorage.setItem('checkotuData', JSON.stringify([cartDetails]))

      let totalWeight = parseFloat(itemWeight) * parseFloat(input.quantity)

      if (totalWeight <= parseFloat("28")) {
        let costObj = {
          orders: [
            {
              supplierID: productData.orgID,
              state: session.profile.primaryAddress.state,
              weight: parseFloat(itemWeight) * parseFloat(input.quantity)
            }
          ]
        }

        setLocalCartState({
          ...localCartState, list: [cartDetails], fromBuyNow: true, shippingCostObj: costObj
        });
        history.push('/checkout');
      } else {
        setWeightValidation(true)
        setTimeout(() => {
          setWeightValidation(false)
        }, 5000);
      }


    }
  }

  const onOpenModal = () => {
    setIsOpen(true);
  }

  const onCloseModal = () => {
    setIsOpen(false);
    handleFetchCart();
  }

  const handleSubmit = async ({ email, password, accountType }) => {
    setLoginStatus({ ...loginStatus, login: 'loading' });

    if (accountType === 'INDIVIDUAL_ACCOUNT') {
      const responseData: any = await authLogin({ email, password });

      if (!isNil(responseData.error)) {
        setLoginStatus({ ...loginStatus, login: 'error' });
        setLoginError(responseData?.error);
      }
      else {
        setLoginStatus({ ...loginStatus, login: 'success' });
        // history.push('/');
      }
    } else if (accountType === 'BUSINESS_ACCOUNT') {
      const responseData: any = await authBusinessAccountLogin({ email, password });

      if (!isNil(responseData.error)) {
        setLoginStatus({ ...loginStatus, login: 'error' });
        setLoginError(responseData?.error);
      }
      else {
        setLoginStatus({ ...loginStatus, login: 'success' });
        // history.push('/');
      }
    }
  }
  const [chatStatusState, setChatStatusState] = useRecoilState<any>(statusChatAtom);

  const chatWithMerchant = () => {
    setChatStatusState({
      ...chatStatusState,
      status: {
        supplier: true,
        product: false,
        order: false
      },
      info: input
    });
  }

  useEffect(() => {
    setChatStatusState({
      ...chatStatusState,
      status: {
        supplier: false,
        product: false,
        order: false
      },
      info: {}
    });
  }, [])

  const handleSwitchCaseVoucherType = (type: string, amount: string) => {
    adjustDropdownTop();
    if (type === "FIAT") {
      return `RM${amount} OFF`;
    } else {
      return `${amount}% OFF`;
    }
  }

  const adjustDropdownTop = () => {
    const dropdown = document.getElementsByClassName("dropdown")[0] as HTMLElement;
    const dropdownContent = document.getElementsByClassName("dropdown-content")[0] as HTMLElement;
    if (dropdown && dropdownContent) {
      dropdownContent.style.top = (dropdown.offsetHeight + 1) + "px";
    }
  }

  const descriptionTab = {
    label: t('tab.description'),
    component: <div className='tab-description' dangerouslySetInnerHTML={{ __html: `<p>${input.product?.description || ''}</p>` }}></div>
  }

  // PRODUCT DETAILS PAGE DESC TABS
  // DEFAULT TABS
  const defaultPdpTabs = [
    descriptionTab,
    {
      label: t('tab.details'),
      component: <div dangerouslySetInnerHTML={{ __html: `<p>${input.ecomDetails || ''}</p>` }}></div>
    },
    {
      label: t('tab.specification'),
      component: <div dangerouslySetInnerHTML={{ __html: `<p>${input.specification || ''}</p>` }}></div>
    },
    {
      label: t('tab.disclaimer'),
      component: <div dangerouslySetInnerHTML={{ __html: `<p>${input.disclaimer || ''}</p>` }}></div>
    }
  ];
  // PHARMACEUTICAL PRODUCTS TABS
  const pharmaPdpTabs = [
    descriptionTab,
    {
      label: t('tab.about'),
      component: <div dangerouslySetInnerHTML={{ __html: `<p>${input.about || ''}</p>` }}></div>
    },
    {
      label: t('tab.precautions'),
      component: <div dangerouslySetInnerHTML={{ __html: `<p>${input.precautions || ''}</p>` }}></div>
    },
    {
      label: t('tab.sideEffects'),
      component: <div dangerouslySetInnerHTML={{ __html: `<p>${input.sideEffects || ''}</p>` }}></div>
    },
    {
      label: t('tab.overviews'),
      component: <div dangerouslySetInnerHTML={{ __html: `<p>${input.overviews || ''}</p>` }}></div>
    },
    {
      label: t('tab.faqs'),
      component: <div dangerouslySetInnerHTML={{ __html: `<p>${input.faqs || ''}</p>` }}></div>
    }
  ]

  const sliderSettings = {
    dots: false,
    infinite:false ,
    speed: 500,
    slidesToShow: 4,
    slidesToScroll: 1
  };

  const handleQuantityChange = (value: string) => {
    // handle non-digit, negative (minus sign), etc.
    value = value.replace(/\D/g, '');

    if (value === '') {
      setInput(prevInput => ({
        ...prevInput,
        quantity: '',
      }));
      return;
    }

    let numValue = parseInt(value);
    if (numValue === 0) return;

    if (numValue > parseInt(input.selectedVariantObj?.restrictedQtyLimit)) {
      toaster('error', t('error.overRestrictedLimit', { amount: parseInt(input.selectedVariantObj?.restrictedQtyLimit) }));
      numValue = parseInt(input.selectedVariantObj?.restrictedQtyLimit);
    }
    if (numValue > parseInt(input.selectedVariantObj?.stockLimit)) {
      numValue = parseInt(input.selectedVariantObj?.stockLimit)
    }

    setInput(prevInput => ({
      ...prevInput,
      quantity: numValue,
    }))
  }

  /**
   * Set the input.quantity to the last input quantity that is a valid number when the input leave focus and is left empty. See Shopee.
   */
  const handleQuantityBlur = () => {
    if (input.quantity === '') {
      setInput(prevInput => ({
        ...prevInput,
        quantity: lastInputQuantity,
      }))
    }
  }

  useEffect(() => {
    if (input.quantity !== '') {
      setLastInputQuantity(input.quantity);
    }
  }, [input.quantity]);

  return (
    <div className="product product-details">
      {!productData?.product ?
        <>
          <SkeletonLoader type="default" />
          <SkeletonLoader type="default" />
          <SkeletonLoader type="default" />
        </>
        :
        <Space direction="vertical" size="large">

          <Row gutter={16} className="product-container">
            <Col lg={8} md={8} sm={24} xs={24}>
              <div className="feature-img">
                <img
                  alt={input?.product?.productName}
                  src={input.featuredImage || input?.images?.[0]?.image || '/assets/images/product-placeholder.png'}
                  onError={({ currentTarget }) => {
                    currentTarget.onerror = null; // prevents looping
                    currentTarget.src = '/assets/images/product-placeholder.png';
                  }}
                />
              </div>

              <div>
                <div className='slider-wrapper'>
                  {input.images &&
                    <Slider {...sliderSettings}>
                    {
                      map(input.images, (imageObj, index) => {
                        return (
                          <div key={index} className="gallery--item" onClick={() => onImageSelect(imageObj)}>
                            <img
                              className={`${imageObj.selected ? 'selected' : ''}`}
                              src={imageObj?.image || '/assets/images/product-placeholder.png'}
                              onError={({ currentTarget }) => {
                                currentTarget.onerror = null; // prevents looping
                                currentTarget.src = '/assets/images/product-placeholder.png';
                              }}
                              width={80}
                            />
                          </div>
                        )
                      })
                    }
                    </Slider>
                  }
                </div>
                <div className="social-share mb-10">
                  <span className="mx-10"> Share: </span>
                  <FacebookShareButton className="share-btn" url={`${process.env.APP_URL}/products/${id}`}>
                    <FacebookIcon className="mr-5" size={36} round={true} />
                  </FacebookShareButton>
                  <WhatsappShareButton className="share-btn"  url={`${process.env.APP_URL}/products/${id}`}>
                    <WhatsappIcon className="mr-5" size={36} round={true} />
                  </WhatsappShareButton>
                  <EmailShareButton className="share-btn" url={`${process.env.APP_URL}/products/${id}`}>
                    <EmailIcon size={36} round={true} />
                  </EmailShareButton>
                </div>
              </div>
            </Col>
            <Col lg={16} md={16} sm={24} xs={24}>
              <div>
                <h1 className="product-heading">{input?.product?.productName}</h1>
                {input?.pharmaProductClause === true && <p className='adviceMsg'>{t('txt.itemAdvisedMsg')}</p>}
              </div>
              <Row gutter={16}>

                <Col lg={16} md={16} sm={24} xs={24}>

                  <div className="variants user-select-none">
                    <p>{t('txt.types')}</p>
                    <Radio.Group
                      options={map(input.stockVariants, (variantObj, i) => {
                        return { label: variantObj.variantName, value: i }
                      })
                      }
                      name="selectedVariant"
                      onChange={(event) => handleVariantRadioChange(event.target.value, 'selectedVariant', 'selectedVariantObj')}
                      value={input.selectedVariant}
                      optionType="button"
                    />
                  </div>
                  {
                    productData.voucherList.length > 0 ?

                      <div className="variants user-select-none">
                        <p>{t('label.Promotions')}</p>
                        <div className="dropdown">
                          <div className="promotion-list">
                            {
                              map(productData.voucherList, (voucher, index) => {
                                return <Radio.Button
                                  style={{ background: "#EE4E2E", borderRadius: "3px", color: "white", fontWeight: "bold" }}
                                  value={index}>
                                  {handleSwitchCaseVoucherType(voucher.discountType, voucher.discountValue)}
                                </Radio.Button>
                              })
                            }
                          </div>
                          <div className="dropdown-content">
                            <VoucherDeals orgId={input.orgID} checkOutPage={false} voucherList={productData.voucherList} handleApplyVoucher={() => { }} />
                          </div>
                        </div>
                      </div> : null
                  }
                  {/* <div className='prices-row my-20'>
                    <h4 className="price-field">
                      RM {parseFloat(input?.selectedVariantObj?.value).toFixed(2)}
                    </h4>
                    {
                      input?.selectedVariantObj?.ssrPrice && discountedPercentageInt > 0 ?
                      <div className='srp-group'>
                        <span className="before-discount">
                          RM {parseFloat(input?.selectedVariantObj?.ssrPrice).toFixed(2)}
                        </span>
                        <span className='discount-percentage'>
                          -{discountedPercentageInt}%
                        </span> 
                      </div>
                      :
                      null
                    }
                  </div> */}
                  <div className='my-20'>
                    <ProductPricing
                      regularPrice={input.selectedVariantObj?.value}
                      srpPrice={input?.selectedVariantObj?.ssrPrice}
                      pricePrefix='RM '
                      variant='lg'
                    />
                  </div>
                  {input.selectedVariantObj?.voucherValidity ? <h5 className="voucher-validity my-5">
                    {t('label.VoucherValidityDays', { count: input.selectedVariantObj?.voucherValidity })}
                  </h5> : null}
                  {productData?.product?.productType === 'NO_SHIPPING' ? <h5 className="items-direct-beneficiary-badge my-5">
                    <RiTruckLine className="truck-icon" size={20}/>
                    <span>{t('label.itemsWillBeSendToBeneficiary')}</span>
                  </h5> : null}
                  <div className="mt-20 user-select-none quantity-div-wrapper">
                    <span>{t('txt.quantity')}</span>
                    <span className="quantity">
                      <MinusCircleOutlined onClick={() => reduceQuantity()} />
                        {/* <span className="px-10">{input.quantity}</span> */}
                        <input
                          className='quantity-input'
                          value={input.quantity}
                          onBlur={e => handleQuantityBlur()}
                          onChange={e => handleQuantityChange(e.target.value)}
                        />
                      <PlusCircleOutlined onClick={() => addQuantity()} />
                    </span>
                    {input.selectedVariantObj?.stockLimit != 0 && !input.disablePurchase && input?.product?.productType != 'VOUCHER' ? <span className="unit-available"> {parseInt(input.selectedVariantObj?.stockLimit)}  {t('txt.unitAvailable')} </span> : null}
                  </div>
                  <Row>
                    <Col lg={24} md={24} sm={24} xs={24} style={{ marginTop: '20px' }}>
                      {weightValidation && <Alert message={t('txt.WeightValidation')} type="warning" showIcon />}
                    </Col>
                    {input.selectedVariantObj?.restrictedQtyLimit &&
                      <Col lg={24} md={24} sm={24} xs={24}>
                        <p className='adviceMsg'>{t('txt.restrictQtyMsg', { qty: parseInt(input.selectedVariantObj?.restrictedQtyLimit) })}</p>
                      </Col>}
                  </Row>
                  {input.disablePurchase || input.selectedVariantObj?.stockLimit == 0 ? <h4 className="color-red">{t('txt.outOfStock')}</h4> : null}
                  <div className="my-20">
                    <Button
                      isLoading={status === "loading"}
                      className="btn-buy"
                      isDisabled={input.disablePurchase || input.selectedVariantObj?.stockLimit == 0}
                      type="primary"
                      label={
                        <React.Fragment>
                          <span>{t('action.buyNow')}</span> <ArrowRightOutlined />
                        </React.Fragment>
                      }
                      size="large"
                      onClick={() => onBuyNow()}
                    />
                    {loadingStatus == false ?
                      <Button
                        className="mx-10 btn-cart"
                        isDisabled={input.disablePurchase || input.selectedVariantObj?.stockLimit == 0}
                        type="secondary"
                        label={t('action.addToCart')}
                        size="large"
                        onClick={() => onAddCart()}
                      />
                      :
                      <Button
                        className="mx-10 btn-cart"
                        isDisabled={true}
                        type="secondary"
                        label='Updating...'
                        size="large"
                      />}
                  </div>

                </Col>

                <Col lg={8} md={8} sm={24} xs={24}>
                  <div className="provider-info">
                    <p>{t('title.merchant')}</p>

                    {/* <Link to={`/products/supplier/${encode(input.orgID)}`}> */}
                    <Link to={`/supplier/${encode(input.orgID)}`}>
                      <img
                        alt={t('alt.logo')}
                        src={input.orgData?.image_file?.file_url || '/assets/images/product-placeholder.png'}
                        width={80}
                      />
                    </Link>

                    {/* <Link to={`/products/supplier/${encode(input.orgID)}`}> */}
                    <Link to={`/supplier/${encode(input.orgID)}`}>
                      <p className="my-10"><strong>{input.orgData?.name}</strong></p>
                    </Link>

                    {profileID ?
                      <div className="my-10 chat-text" onClick={() => chatWithMerchant()}>
                        <img src='/assets/images/chat-icons/chatbox-03.svg' width={15} />&nbsp;<span className='chat-text-txt'>Chat With Merchant</span>
                      </div> : null}

                    {input.orgData?.primaryAddress?.city ?
                      <div className="my-10">
                        <img src='/assets/images/location.svg' width={15} />&nbsp;{input.orgData?.primaryAddress?.city}
                      </div> : null}

                    {
                      // if is vacation
                      input.orgData?.ecomSetting?.vacationMode &&
                      <VacationInfoBox
                        infoOption={input.orgData?.ecomSetting?.vacationOrderHandling}
                        type={
                          input.orgData?.ecomSetting?.vacationOrderHandling === "CHECKOUT_NOT_ALLOWED" ?
                            "warning" :
                            "info"
                        }
                      />
                    }

                    {input.orgData?.workingHours &&
                      // hide if is on vacation
                      !input.orgData?.ecomSetting?.vacationMode ?
                      <div className="my-10">
                        <p className="my-5"><strong>{t('label.workingHours')}:</strong></p>
                        <div dangerouslySetInnerHTML={{ __html: `<p className="my-5">${input.orgData?.workingHours}</p>` }}></div>

                      </div> : null}
                    {/*<p>{t('txt.rating')} : N/A</p>
                  <p>{t('txt.established')}: N/A</p>*/}

                    {/*<Divider />
                  <p>{t('txt.products')}: <a>N/A</a></p>
                  <p>{t('txt.services')}: <a>N/A</a></p>
                  <p><a>{t('txt.contactProvider')}</a></p>
                  <p><a>{t('txt.chatWthProvider')}</a></p>*/}

                  </div>


                </Col>
              </Row>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col offset={2} span={20}>
              <Tabs
                className="bind-html"
                centered={false}
                // onTabClick={handleTabClick}
                tabBarGutter={10}
                tabs={input?.isPharmaProduct === true ? pharmaPdpTabs : defaultPdpTabs}
                defaultActiveKey="0"
                size="large"
              />
            </Col>
          </Row>
        </Space>
      }
      <BasicModal title="Login" isClosable={true} isMaskClosable={true} isOpen={isOpen} onClose={onCloseModal}>
        <LoginForm
          status={loginStatus.login}
          expectedError={loginError}
          onSubmit={handleSubmit}
          onSuccess={() => {
            onCloseModal();
            toaster('success', t('txt.loginSuccess'));
            setLoginStatus({ ...loginStatus, login: 'idle' });
          }}
        />
      </BasicModal>
    </div>
  )
}


export default ProductDetails;
